import React, { useState } from 'react';
import { ChatSession } from '../AiChatSlideout';
import { ReactComponent as ChatIcon } from '../../../shared/icons/chat-icon.svg';
import { ReactComponent as DocumentIcon } from '../../../shared/icons/document-icon.svg';
import { ReactComponent as PlusIcon } from '../../../shared/icons/plus-icon.svg';
import { ReactComponent as TrashIcon } from '../../../shared/icons/trashcan-icon.svg';
import { DeleteChatConfirmationModal } from './DeleteChatConfirmationModal';
import styles from './ChatHistory.module.scss';

interface ChatHistoryProps {
  sessions: ChatSession[];
  onSelectSession: (session: ChatSession) => void;
  onNewChat: () => void;
  onDeleteSession: (sessionId: string) => void;
  isDeletingSession?: boolean;
}

export const ChatHistory: React.FC<ChatHistoryProps> = ({
  sessions,
  onSelectSession,
  onNewChat,
  onDeleteSession,
  isDeletingSession = false,
}) => {
  const [sessionToDelete, setSessionToDelete] = useState<ChatSession | null>(null);
  const formatDate = (date: Date) => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 1) {
      return 'Just now';
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else {
      return d.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  return (
    <div className={styles.chatHistory}>
      {/* New Chat Button */}
      <div className={styles.newChatContainer}>
        <button
          onClick={onNewChat}
          className={styles.newChatButton}
          type="button"
        >
          <PlusIcon />
          <span>New Chat</span>
        </button>
      </div>

      {/* Sessions List */}
      <div className={styles.sessionsList}>
        {sessions.length === 0 ? (
          <div className={styles.emptyState}>
            <ChatIcon />
            <p>No chat history yet</p>
            <p className={styles.hint}>Start a new chat to begin</p>
          </div>
        ) : (
          sessions.map((session) => (
            <div
              key={session.id}
              className={styles.sessionCard}
              onClick={() => onSelectSession(session)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  onSelectSession(session);
                }
              }}
            >
              <div className={styles.sessionHeader}>
                <span className={styles.sessionTitle}>
                  {session.title}
                </span>
                <div className={styles.sessionActions}>
                  <span className={styles.sessionDate}>
                    {formatDate(session.updatedAt)}
                  </span>
                  <button
                    className={styles.deleteButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSessionToDelete(session);
                    }}
                    aria-label="Delete chat"
                    type="button"
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>
              
              {session.documentName && (
                <div className={styles.sessionDocument}>
                  <DocumentIcon />
                  <span>{session.documentName}</span>
                </div>
              )}
              
              {session.lastMessage && (
                <div className={styles.sessionPreview}>
                  {session.lastMessage}
                </div>
              )}
              
              <div className={styles.sessionMeta}>
                <span className={styles.messageCount}>
                  {session.messageCount} message{session.messageCount !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
      
      <DeleteChatConfirmationModal
        session={sessionToDelete}
        onConfirm={() => {
          if (sessionToDelete) {
            onDeleteSession(sessionToDelete.id);
            setSessionToDelete(null);
          }
        }}
        onCancel={() => setSessionToDelete(null)}
        isDeleting={isDeletingSession}
      />
    </div>
  );
};